import { cardText, Regime } from 'components/ExportDesign/exportDesignResultCardData';
import { humanizeKey, humanizeSpecRef } from 'components/ExportDesign/exportDesignProgress';

// --- The change path and what-if cards' data (UX slice S7, boards 06/07/09). The backend sends them as
// `data.change_path` (the delta's answer) and `data.what_if` (the what-if's answer); the markdown answer is
// still sent and stays the fallback. Read defensively: a stored chat is data, not trusted. The actions
// (summary, save, another target, test the change) live in the S3 run-options block under the card (D-25).

export interface CardPlace {
  regime: string;
  control?: string | null;
  citations: string[];
  nature_function?: boolean;
}

export interface CardChange {
  constraint_citation?: string | null;
  change_type?: string | null;
  parameter?: string | null;
  label?: string | null;
  current_value?: string | null;
  proposed_value?: string | null;
  description?: string | null;
  rationale?: string | null;
  feasibility?: string | null;
  destroys_capability?: boolean;
}

export interface CardCaveat {
  missing_spec?: string | null;
  assumed?: string | null;
  impact?: string | null;
}

export interface ChangePathPayload {
  delta: {
    target_regime: string;
    reachable: boolean;
    infeasible_reason?: string | null;
    changes: CardChange[];
    residual_constraints: string[];
    resulting_regime?: string | null;
    functional_impact?: string | null;
    must_keep_conflict?: boolean;
    explanation?: string | null;
  };
  from: CardPlace;
  to: { regime: string; resulting_regime?: string | null };
  caveats: CardCaveat[];
  test_prompt?: string | null;
  /** S12 R-14: the target is MORE controlled; the card reads "what would make it X, to stay clear of". */
  toward_more_control?: boolean;
}

export interface WhatIfSpecRow {
  key: string;
  label: string;
  before: string | null;
  after: string | null;
  changed: boolean;
}

export interface WhatIfPayload {
  before: CardPlace;
  after: CardPlace;
  moved: boolean;
  specs: WhatIfSpecRow[];
  more_specs: number;
  still_trips?: { citation?: string | null; spec?: string | null } | null;
  go_back_prompt?: string | null;
}

const isString = (value: unknown): value is string => typeof value === 'string';
const isObject = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown): string | null => (isString(value) && value.trim() ? value : null);
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => isString(v) && Boolean(v.trim())) : [];

const readPlace = (raw: unknown): CardPlace | undefined => {
  if (!isObject(raw) || !isString(raw.regime)) return undefined;
  return {
    regime: raw.regime,
    control: text(raw.control),
    citations: strings(raw.citations),
    nature_function: raw.nature_function === true,
  };
};

/** `data.change_path`, or undefined when it isn't one (the markdown answer is shown instead). */
export const readChangePath = (input: unknown): ChangePathPayload | undefined => {
  const raw = input as any;
  if (!isObject(raw) || !isObject(raw.delta) || typeof raw.delta.reachable !== 'boolean') return undefined;
  const from = readPlace(raw.from);
  if (!from) return undefined;
  const d = raw.delta;
  return {
    delta: {
      target_regime: text(d.target_regime) || 'the target',
      reachable: d.reachable,
      infeasible_reason: text(d.infeasible_reason),
      changes: (Array.isArray(d.changes) ? d.changes : []).filter(isObject).map(
        (c: any): CardChange => ({
          constraint_citation: text(c.constraint_citation),
          change_type: text(c.change_type),
          parameter: text(c.parameter),
          label: text(c.label),
          current_value: text(c.current_value),
          proposed_value: text(c.proposed_value),
          description: text(c.description),
          rationale: text(c.rationale),
          feasibility: text(c.feasibility),
          destroys_capability: c.destroys_capability === true,
        }),
      ),
      residual_constraints: strings(d.residual_constraints),
      resulting_regime: text(d.resulting_regime),
      functional_impact: text(d.functional_impact),
      must_keep_conflict: d.must_keep_conflict === true,
      explanation: text(d.explanation),
    },
    from: from,
    to: {
      regime: isObject(raw.to) && isString(raw.to.regime) ? raw.to.regime : text(d.target_regime) || 'the target',
      resulting_regime: isObject(raw.to) ? text(raw.to.resulting_regime) : null,
    },
    caveats: (Array.isArray(raw.caveats) ? raw.caveats : [])
      .filter((c: any) => isObject(c) && text(c.missing_spec))
      .map((c: any) => ({ missing_spec: text(c.missing_spec), assumed: text(c.assumed), impact: text(c.impact) })),
    test_prompt: text(raw.test_prompt),
    toward_more_control: raw.toward_more_control === true,
  };
};

/** `data.what_if`, or undefined when it isn't one. */
export const readWhatIf = (input: unknown): WhatIfPayload | undefined => {
  const raw = input as any;
  if (!isObject(raw)) return undefined;
  const before = readPlace(raw.before);
  const after = readPlace(raw.after);
  if (!before || !after) return undefined;
  return {
    before: before,
    after: after,
    moved: raw.moved === true,
    specs: (Array.isArray(raw.specs) ? raw.specs : [])
      .filter((s: any) => isObject(s) && isString(s.key))
      .map((s: any) => ({
        key: s.key,
        // The chat's own label for a key (as on the result card), not the backend's.
        label: humanizeKey(s.key),
        before: s.before === null || s.before === undefined ? null : String(s.before),
        after: s.after === null || s.after === undefined ? null : String(s.after),
        changed: s.changed === true,
      })),
    more_specs: typeof raw.more_specs === 'number' && raw.more_specs > 0 ? raw.more_specs : 0,
    still_trips: isObject(raw.still_trips)
      ? { citation: text(raw.still_trips.citation), spec: text(raw.still_trips.spec) }
      : null,
    go_back_prompt: text(raw.go_back_prompt),
  };
};

// ----------------------------------------------------------------------------------- view helpers

const REGIMES: Regime[] = ['ITAR', 'EAR-listed', 'EAR99', 'uncertain'];

export const REGIME_CLASS: Record<string, string> = {
  ITAR: 'regime-itar',
  'EAR-listed': 'regime-ear-listed',
  EAR99: 'regime-ear99',
  uncertain: 'regime-uncertain',
};

/** "EAR-listed · ECCN 3A001.a.1.a", "EAR99": where a classification sits, in a line. */
export const placeLabelOf = (place: CardPlace): string =>
  place.control ? `${place.regime} · ${place.control}` : place.regime;

/** The regime class for a free-text regime ("EAR99 (decontrolled)" → regime-ear99). */
export const regimeClassOf = (regime?: string | null): string => {
  const value = (regime || '').trim();
  const found = REGIMES.find((r) => value.toUpperCase().startsWith(r.toUpperCase()));
  return REGIME_CLASS[found || 'uncertain'];
};

export interface ChangePathView {
  /** S12 R-14: toward a more controlled regime (what to stay clear of). */
  towardMore: boolean;
  title: string;
  sub: string;
  pill: string;
  pillClass: string;
  after: string;
  tradeOff: boolean;
}

/** Board 06/07 headline, pill and route end from `reachable` and the trade-offs. */
export const changePathViewOf = (card: ChangePathPayload): ChangePathView => {
  const d = card.delta;
  const target = card.to.regime || d.target_regime;
  if (card.toward_more_control) {
    // S12 R-14 (D-38): the designer wants to know what to stay clear of, not how to get there.
    const n = d.changes.length;
    return d.reachable
      ? {
          towardMore: true,
          title: `What would make it ${target}`,
          sub:
            n > 0
              ? `${n === 1 ? 'This change' : 'Each of these changes'} would put it there. Stay clear of ${n === 1 ? 'it' : 'them'} to keep it ${card.from.regime}.`
              : 'A change to its design could put it there. The reasoning is below.',
          // QA-3: the pill must read on its own ("To stay clear of" didn't).
          pill: 'Changes to avoid',
          pillClass: 'is-warn',
          after: `Would be ${d.resulting_regime && d.resulting_regime.length <= 40 ? d.resulting_regime : target}`,
          tradeOff: false,
        }
      : {
          towardMore: true,
          title: `Nothing in its design would make it ${target}`,
          sub: 'No change it could take would put it there. Why:',
          pill: `Stays ${card.from.regime}`,
          pillClass: regimeClassOf(card.from.regime),
          after: `${target}, not reachable`,
          tradeOff: false,
        };
  }
  if (!d.reachable) {
    const why = card.from.nature_function
      ? `${card.from.control || card.from.regime} controls it for what it is, not for a number you could change.`
      : 'No change it could take and still be this product clears its controls. Why, and what you can do instead:';
    return {
      towardMore: false,
      title: `No design change moves it to ${target}`,
      sub: why,
      pill: `Stays ${card.from.regime}`,
      pillClass: regimeClassOf(card.from.regime),
      after: `${target}, not reachable`,
      tradeOff: false,
    };
  }
  const n = d.changes.length;
  const tradeOff = Boolean(d.must_keep_conflict) || d.changes.some((c) => c.destroys_capability);
  return {
    towardMore: false,
    title: n > 0 ? `${target} is reachable with ${n === 1 ? 'one change' : `${n} changes`}` : `${target} looks reachable`,
    sub: tradeOff
      ? 'But it cuts into something the product must do. Read the trade-off before deciding.'
      : 'A design change only counts once the changed product is classified. Test it before relying on it.',
    pill: tradeOff ? 'Reachable, with a trade-off' : 'Reachable',
    pillClass: tradeOff ? 'is-warn' : 'is-ok',
    // The model sometimes writes the resulting regime as a sentence; only a short regime name fits the route.
    after: `Likely ${d.resulting_regime && d.resulting_regime.length <= 40 ? d.resulting_regime : target}`,
    tradeOff: tradeOff,
  };
};

/** A change's spec name: the backend's label, else the key humanized, else "This change". */
export const changeNameOf = (change: CardChange): string =>
  cardText(change.label) || (change.parameter ? humanizeSpecRef(change.parameter) : '') || '';

export interface WhatIfView {
  title: string;
  sub: string;
  goBackLabel: string | null;
}

/** Board 09 headline and the "go back" button label. */
export const whatIfViewOf = (card: WhatIfPayload): WhatIfView => {
  const now = card.after.control || card.after.regime;
  const changed = card.specs.filter((s) => s.changed);
  let goBackLabel: string | null = null;
  if (card.go_back_prompt) {
    goBackLabel =
      changed.length === 1 && changed[0].before ? `Go back to ${changed[0].before}` : 'Undo these changes';
  }
  if (card.moved) {
    return {
      title: card.after.regime === 'uncertain' ? 'With that change it isn’t settled yet' : `With that change it would likely be ${now}`,
      sub: `Before: ${placeLabelOf(card.before)}. Re-classified with your change; the full result is below.`,
      goBackLabel: goBackLabel,
    };
  }
  const trips = card.still_trips;
  return {
    title: `Still ${now}`,
    sub: trips?.citation
      ? `${trips.citation} still applies${trips.spec ? ` (it trips on: ${trips.spec})` : ''}.`
      : 'This change doesn’t move the classification.',
    goBackLabel: goBackLabel,
  };
};
